"use client";

import { CaretDown, CaretUp, List, Phone, User } from "@phosphor-icons/react";
import Image from "next/image";
import React, { useEffect, useRef, useState } from "react";
import MobileMenu from "./MobileNav";
import LeadForm from "../LeadForm";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { check_auth, getStudentDetail, logoutStudent } from "@/api";

const getCoursePath = (course) => {
  const id = course?.id || course?._id || course?.course_id || course?.slug;
  return id ? `/courses/${id}` : null;
};

const Navbar = ({ courses }) => {
  const [openDropdown, setOpenDropdown] = useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrollingUp, setIsScrollingUp] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [studentName, setStudentName] = useState("");

  const dropdownRef = useRef(null);
  const lastScrollY = useRef(0);

  const pathname = usePathname();
  const router = useRouter();
  const isDashboardOrLearn = pathname.startsWith('/dashboard') || pathname.startsWith('/learn') || pathname.startsWith('/admin');

  const isHomePage = pathname === '/';

  const toggleDropdown = (menu) => {
    setOpenDropdown(openDropdown === menu ? "" : menu);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY < lastScrollY.current) {
        setIsScrollingUp(true); 
      } else {
        setIsScrollingUp(false);
      }
      lastScrollY.current = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const syncAuth = async () => {
      try {
        const session = await check_auth();
        if (!session) {
          setIsLoggedIn(false);
          setStudentName("");
          return;
        }
        const user = await getStudentDetail();
        setIsLoggedIn(true);
        setStudentName(user?.name || "");
      } catch {
        setIsLoggedIn(false);
        setStudentName("");
      }
    };

    syncAuth();
  }, [pathname]);

  const handleLogout = async () => {
    try {
      await logoutStudent();
    } catch {
      // ignore
    } finally {
      setIsLoggedIn(false);
      setStudentName("");
      router.replace("/");
    }
  };

  return (
    <div
      className={`sticky z-10 transition-all duration-300
        ${
          isScrollingUp
            ? isHomePage
              ? "top-0 md:top-[64px]"
              : "top-0"
            : "top-0"
        }`}
    >
      <div className="flex justify-between items-center w-full py-[18px] px-[16px] md:px-[80px] bg-blue-20 relative">
        <div className="flex items-center gap-2">
          <a href="/">
            <Image
              src={"/sofar_logo.svg"}
              alt="Logo"
              width={96}
              height={34}
              loading="eager"
            />
          </a>
        </div>
        {!isDashboardOrLearn && (
          <nav
            ref={dropdownRef}
            className="hidden lg:flex items-center gap-8 relative"
          >
              <div className="relative">
                <button
                  onClick={() => toggleDropdown("courses")}
                  className="flex items-center gap-[8px] text-[#FFFFFF] font-[500] text-[16px] cursor-pointer"
                >
                  Courses
                  {openDropdown === "courses" ? (
                    <CaretUp size={20} />
                  ) : (
                    <CaretDown size={20} />
                  )}
                </button>
                <div
                  className={`
                  absolute left-1/2 flex flex-col items-center transform -translate-x-1/2 top-full mt-2 w-48 bg-white text-black shadow-lg rounded-md z-20 transition-all duration-200 origin-top
                  ${
                    openDropdown === "courses"
                      ? "scale-100 opacity-100 delay-100 duration-500"
                      : "scale-95 opacity-0 delay-100 duration-500 pointer-events-none"
                  }
                `}
                >
                  {courses.length === 0 ? (
                    <p>..</p>
                  ) : (
                    courses.map((course, index) => (
                      <Link
                        key={index}
                        href={getCoursePath(course) || "#"}
                        className={`block px-4 py-2 leading-[110%] ${
                          getCoursePath(course) ? "" : "pointer-events-none opacity-50"
                        }`}
                      >
                        {course?.title}
                      </Link>
                    ))
                  )}
                </div>
              </div>

              <Link href="/blog" className="text-[#FFFFFF] font-[500] text-[16px]">
                Blog
              </Link>

              <a href="/#about-us" className="text-[#FFFFFF] font-[500] text-[16px]">
                About Us
              </a>
              <button onClick={() => setShowForm(true)}>
                <a href="#" className="text-[#FFFFFF] font-[500] text-[16px]">
                  Contact Us
                </a>
              </button>
          </nav>
        )}
        <div className="hidden lg:flex items-center gap-[8px]">
          {!isDashboardOrLearn && (
            <a
              href="tel:+919220361603"
              className="flex h-[40px] w-[40px] items-center justify-center rounded-[4px] bg-white text-blue-00 hover:text-black"
              aria-label="Call SOFAR"
            >
              <Phone size={20} weight="fill" />
            </a>
          )}
          {isLoggedIn ? (
            <>
              {!isDashboardOrLearn && (
                <Link href="/dashboard">
                  <button className="px-[16px] py-[8px] rounded-[4px] text-blue-00 bg-white hover:text-black cursor-pointer text-[16px] font-[400]">
                    {studentName || "Dashboard"}
                  </button>
                </Link>
              )}
              <button
                onClick={handleLogout}
                className="px-[16px] py-[8px] rounded-[4px] bg-primary text-white btn-primary cursor-pointer text-[16px] font-[400] flex items-center gap-[8px]"
              >
                Logout
              </button>
            </>
          ) : (
            <>
              <Link href="/register">
                <button className="px-[16px] py-[8px] rounded-[4px] text-blue-00 bg-white hover:text-black cursor-pointer text-[16px] font-[400]">
                  Sign Up
                </button>
              </Link>

              <Link href="/login">
                <button className="px-[16px] py-[8px] rounded-[4px] bg-primary text-white btn-primary cursor-pointer text-[16px] font-[400] flex items-center gap-[8px]">
                  <User size={24} />
                  Login
                </button>
              </Link>
            </>
          )}
        </div>

        <button
          className="block lg:hidden text-white"
          onClick={() => setMobileMenuOpen(true)}
        >
          <List size={32} />
        </button>
      </div>

      {mobileMenuOpen && (
        <MobileMenu
          onClose={() => setMobileMenuOpen(false)}
          courses={courses}
          isLoggedIn={isLoggedIn}
          onLogout={handleLogout}
          isDashboardOrLearn={isDashboardOrLearn}
        />
      )}

      {showForm && <LeadForm onClose={() => setShowForm(false)} />}
    </div>
  );
};

export default Navbar;
